import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TestResult } from '../../../shared/test.types';
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { ReactionTrial } from '../../sharp-v2.types';
import { SharpAudioService } from '../../services/sharp-audio.service';
import { SharpScoringService } from '../../services/sharp-scoring.service';
import { SharpStateService } from '../../services/sharp-state.service';

type Phase = 'intro' | 'task' | 'interrupted' | 'done';

// Shortened PVT-B (Basner 2011). Handoff 2026-08-17 (part B): the reaction task is
// a psychomotor vigilance test, not the old star-catch game. A circle lights up
// after a variable 2-4s wait; PINK = tap fast, BLUE (one in five) = a no-go the
// user must NOT respond to. The long, unpredictable interval is what makes it a
// vigilance measure - a short interval makes the onset predictable and destroys it.
const CFG = {
  trials: 6, // go-trials to collect before the (single) auto-extension check
  extraTrials: 2, // opened once if the measurement is unstable
  isiMin: 2000,
  isiMax: 4000,
  respWindow: 1800,
  catchAt: 5, // every 5th stimulus is a blue no-go
  sdThreshold: 180,
  slowFlag: 600,
  lapseMs: 355, // a response at/above this is a lapse
  catchHold: 900, // how long a blue no-go stays before advancing
};

@Component({
  selector: 'app-constellation-catch',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  templateUrl: './constellation-catch.component.html',
  styleUrls: ['./constellation-catch.component.scss'],
})
export class ConstellationCatchComponent implements OnInit, OnDestroy {
  @Input() testId = 'reaction';
  @Output() testComplete = new EventEmitter<TestResult>();

  phase: Phase = 'intro';

  dotState: '' | 'pink' | 'blue' = ''; // '' = dark / idle
  // Danna 2026-08-17: a valid pink tap needs a visible confirmation — the circle
  // used to go dark instantly, so a fast tap felt like nothing registered. This
  // fires a brief "got it" burst on the circle for the ~300ms before the next
  // stimulus. Only on a real go-tap (not a blue no-go / premature tap).
  justTapped = false;
  live = false;
  isCatch = false;
  waiting = false; // shows the "wait..." hint between stimuli
  extendMessage = false;
  target = CFG.trials;
  trialDots: boolean[] = [];

  private rts: number[] = [];
  private falseStarts = 0;
  private misses = 0;
  private extended = false;
  private stim = 0; // total stimuli shown (go + catch), drives the catch cadence
  private goShown = 0; // resolved go-trials (a tap or a miss)
  private showTime = 0;
  private startTime = 0;
  private done = false;
  private interruptions = 0;

  private isiTimer: any = null;
  private winTimer: any = null;
  private stepTimer: any = null;

  constructor(
    private audio: SharpAudioService,
    private scoring: SharpScoringService,
    private state: SharpStateService
  ) {}

  ngOnInit(): void {
    this.startTime = Date.now();
    this.trialDots = Array.from({ length: CFG.trials }, () => false);
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  ngOnDestroy(): void {
    this.clearTimers();
    document.removeEventListener('visibilitychange', this.onVisibility);
  }

  private readonly onVisibility = () => {
    if (document.visibilityState === 'hidden') this.interrupt();
  };

  /**
   * Correction brief 3.3: a call, a notification or an app switch while the circle
   * is due used to be scored - an unseen pink became a miss and a lapse. The
   * stimulus in flight is now thrown away (no RT, no miss, no false start) and the
   * run carries on from the same trial once she is back. Resolved trials stay.
   */
  private interrupt(): void {
    if (this.done || this.phase !== 'task') return;
    this.clearTimers();
    this.live = false;
    this.waiting = false;
    this.justTapped = false;
    this.dotState = '';
    this.interruptions++;
    this.phase = 'interrupted';
  }

  resumeAfterInterrupt(): void {
    if (this.phase !== 'interrupted') return;
    this.phase = 'task';
    this.next();
  }

  private clearTimers(): void {
    [this.isiTimer, this.winTimer, this.stepTimer].forEach(
      (t) => t && clearTimeout(t)
    );
    this.isiTimer = this.winTimer = this.stepTimer = null;
  }

  get counterParams() {
    return {
      current: Math.min(this.goShown + 1, this.target),
      total: this.target,
    };
  }

  startTask(): void {
    if (this.phase !== 'intro') return;
    this.phase = 'task';
    this.next();
  }

  onTap(ev: Event): void {
    ev.preventDefault();
    if (this.phase !== 'task') return;
    if (!this.live) {
      // Tap while the circle is dark = premature (a false start). Not scored, but
      // it feeds the auto-extension decision.
      this.falseStarts++;
      return;
    }
    if (this.winTimer) clearTimeout(this.winTimer);
    if (this.isCatch) {
      // Tapped on a blue no-go. Not scored; recorded as a false start.
      this.falseStarts++;
      this.live = false;
      this.dotState = '';
      this.stepTimer = setTimeout(() => this.next(), 280);
      return;
    }
    // Valid go response.
    const rt = performance.now() - this.showTime;
    this.rts.push(rt);
    this.audio.hit();
    this.resolveGo();
    this.live = false;
    this.dotState = '';
    this.justTapped = true; // visible confirmation burst for the gap before next()
    this.stepTimer = setTimeout(() => {
      this.justTapped = false;
      this.next();
    }, 300);
  }

  private resolveGo(): void {
    if (this.goShown < this.trialDots.length) this.trialDots[this.goShown] = true;
    this.goShown++;
  }

  private next(): void {
    if (this.done) return;
    if (this.goShown >= this.target) {
      this.maybeExtendOrFinish();
      return;
    }
    const isCatch = this.stim % CFG.catchAt === CFG.catchAt - 1;
    this.stim++;
    this.isCatch = isCatch;
    this.waiting = true;
    this.dotState = '';
    this.live = false;
    this.isiTimer = setTimeout(() => {
      this.waiting = false;
      this.live = true;
      this.showTime = performance.now();
      this.audio.tick();
      if (isCatch) {
        this.dotState = 'blue';
        this.winTimer = setTimeout(() => {
          // No-go passed (no response) - correct, nothing recorded.
          this.live = false;
          this.dotState = '';
          this.stepTimer = setTimeout(() => this.next(), 280);
        }, CFG.catchHold);
      } else {
        this.dotState = 'pink';
        this.winTimer = setTimeout(() => {
          // Miss: a go-trial with no response inside the window.
          this.live = false;
          this.misses++;
          this.resolveGo();
          this.dotState = '';
          this.stepTimer = setTimeout(() => this.next(), 280);
        }, CFG.respWindow);
      }
    }, this.scoring.randInt(CFG.isiMin, CFG.isiMax));
  }

  private maybeExtendOrFinish(): void {
    if (!this.extended) {
      const sd = this.scoring.stddev(this.rts);
      const med = this.scoring.median(this.rts);
      if (
        sd > CFG.sdThreshold ||
        med > CFG.slowFlag ||
        this.falseStarts >= 2
      ) {
        this.extended = true;
        this.target += CFG.extraTrials;
        while (this.trialDots.length < this.target) this.trialDots.push(false);
        this.extendMessage = true;
        this.stepTimer = setTimeout(() => this.next(), 900);
        return;
      }
    }
    this.finish();
  }

  private finish(): void {
    if (this.done) return;
    this.done = true;
    this.phase = 'done';
    this.clearTimers();
    this.audio.success();
    const rts = this.rts;
    const median = this.scoring.round(this.scoring.median(rts), 1);
    const mean = this.scoring.round(this.scoring.mean(rts), 1);
    const sd = this.scoring.round(this.scoring.stddev(rts), 1);
    // SD is computed (it feeds CV, which the score uses) but never shown: with six
    // trials it can't be read as intra-individual variability, so the UI reports
    // only the median + lapses (handoff part B).
    const cv =
      rts.length && mean > 0 ? this.scoring.round((sd / mean) * 100, 1) : 0;
    const lapses = rts.filter((r) => r >= CFG.lapseMs).length;
    if (!rts.length) {
      // No valid response: a zero here would read like a measurement, which is
      // exactly what must not happen. Flag it so downstream can tell it apart.
      this.state.addValidityFlag('Reaction: no valid response');
    }
    if (this.interruptions) {
      this.state.addValidityFlag(`Reaction interrupted: ${this.interruptions}`);
    }
    if (this.falseStarts >= 3) {
      this.state.addValidityFlag(`Reaction false starts: ${this.falseStarts}`);
    }
    const trials: ReactionTrial[] = rts.map((rt, i) => ({
      trial: i + 1,
      rt: this.scoring.round(rt, 1),
      hit: true,
    }));
    const response = {
      trials,
      median,
      mean,
      sd,
      cv,
      lapses,
      maxStreak: rts.length,
      interruptions: this.interruptions,
    };
    const result: TestResult = {
      testId: this.testId,
      response,
      correct: median > 0 && median < 600,
      responseTime: Date.now() - this.startTime,
      timestamp: Date.now(),
    };
    this.testComplete.emit(result);
  }
}
